<script lang="ts" setup>
import { faLanguage, faLink, faPlus, faSpinnerThird, type IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import type { MailTemplateTranslation } from '~/services/MailTemplateService';
import { validate } from "uuid"
import { faMagicWandSparkles } from '@fortawesome/pro-duotone-svg-icons';
import confetti from "canvas-confetti";
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { BubbleMenu } from "@tiptap/vue-3";

const show = defineModel<boolean>("show", {
  required: true
})

const store = useMailStore();
const editingId = ref<string | number | null>(null);
const usedLocales = computed(() => [{ locale: store.locale }, ...(store.translations ?? [])].map(t => t.locale?.id).filter(id => !!id));
const { t } = useI18n();
const translatables = ref<{
  key: string;
  label: string;
  icon: IconDefinition | null;
  defaultValue: string | object | null;
  value: string | object | null;
  update: (value: any) => void;
}[]>([]);

function computeTranslatables() {
  const defaultTranslateables = [
    {
      key: 'subject',
      label: 'Subject',
      icon: null,
      defaultValue: store.subject,
      value: store.editingTranslation?.subject,
      update: (value: string) => {
        store.editingTranslation!.subject = value;
      }
    },
    {
      key: 'name',
      label: 'Name',
      icon: null,
      defaultValue: store.name,
      value: store.editingTranslation?.name,
      update: (value: string) => {
        store.editingTranslation!.name = value;
      }
    }
  ];

  translatables.value = [...defaultTranslateables, ...Object.entries(store.translateables ?? {}).map(([key, value]) => {
    if (value && typeof value === "object") {
      value = {
        "type": "doc",
        "content": [{
          "type": "paragraph",
          "content": value
        }]
      };
    }

    let translation: any = store.editingTranslation?.translations[key];

    if (value && typeof value === "object") {
      translation = {
        type: "doc",
        content: [{
          type: "paragraph",
          content: translation ?? []
        }]
      };
    } else if (typeof translation === "string") {
      translation = translation.trim();
    }

    let label = key;
    let icon = null;
    if (validate(label.split(".")[0])) {
      const { block, chain } = store.getBlockById(label.split(".")[0]);

      if (block) {
        label = ''

        for (const chainItem of chain) {
          label += t(`mail.blocks.breadcrumb.${chainItem.type}`, { level: chainItem.attrs?.level ?? 1 }) + " > ";
        }

        label += t(`mail.blocks.labels.${block.type}`) + `: ${key.split(".").slice(1).map(part => {
          // The part can be in camelCase, so we need to convert it to title case
          part = part.replace(/([A-Z])/g, ' $1').trim();
          part = part.charAt(0).toUpperCase() + part.slice(1);

          return part;
        }).join(" ")}`;
        icon = mailBlockTypeIcons[block.type as keyof typeof mailBlockTypeIcons];
      }
    } else {
      label = label.charAt(0).toUpperCase() + label.slice(1);
    }

    return {
      key,
      label,
      icon,
      defaultValue: value,
      value: translation ?? "",
      update: (value: any) => {
        if (typeof value === "object") {
          value = value.content[0].content;
        }

        store.editingTranslation!.translations[key] = value;
      }
    };
  })];
}

function edit(translation: MailTemplateTranslation) {
  store.editingTranslation = translation ? useCloneDeep(translation) : null;
  editingId.value = translation?.id ?? null;
  computeTranslatables();
}

function cancel() {
  store.editingTranslation = null;
  editingId.value = null;
  computeTranslatables();
}

function create() {
  store.editingTranslation = {
    id: null,
    locale: {
      id: null
    },
    subject: null,
    name: null,
    description: null,
    translations: {},
  } as unknown as MailTemplateTranslation;
  editingId.value = store.editingTranslation.id;
  computeTranslatables();
}

const aiCanvas = ref<(HTMLCanvasElement & { confetti: any }) | null>(null);

const api = useApi();
const { resume, pause, isActive } = useIntervalFn(() => {
  if (!aiCanvas.value) return;

  aiCanvas.value.confetti = aiCanvas.value.confetti || confetti.create(aiCanvas.value, { resize: true });

  aiCanvas.value?.confetti({
    spread: 50,
    origin: { y: -1 },
    colors: ['#ffffff'],
    shapes: ['circle'],
    scalar: 0.5,
    particleCount: 200,
    gravity: 2,
  });
}, 750, {
  immediate: false,
  immediateCallback: true
});

const lastUpdated = ref<number>(Date.now());

async function createAiTranslation() {
  if (!store.editingTranslation) return;

  resume();

  const response = await api.mail_templates.generateTranslation(store.mail.id, store.editingTranslation!.locale.id).catch(() => {
    return {
      data: {}
    };
  }) as { data: Record<string, string | object | null> };

  store.editingTranslation!.subject = ((response.data).subject ?? null) as string | null;
  store.editingTranslation!.name = ((response.data).name ?? null) as string | null;
  store.editingTranslation!.translations = {
    ...store.editingTranslation!.translations,
    ...(Object.fromEntries(Object.entries(response.data).filter(([key]) => !['subject', 'name'].includes(key))) as Record<string, string | object | null>),
  } as Record<string, string | object | null>;

  lastUpdated.value = Date.now();

  pause();
}

watch(show, (to, from) => {
  if (to && !from) {
    computeTranslatables();
  } else if (!to && from) {
    store.editingTranslation = null;
  }
});

watch([() => store.jsonData, isActive, () => store.name, () => store.subject, () => store.saving], () => {
  computeTranslatables();
}, {
  deep: true
});
</script>

<template>
  <AutomationSideCard class="max-w-md w-full" :on-close="() => (show = false)" :disabled="isActive">
    <template #title>
      <div v-if="store.editingTranslation" class="flex items-center gap-3 font-display">
        <FontAwesomeIcon :icon="faLanguage" />
        <span>{{ store.editingTranslation?.id ? store.editingTranslation.locale.name : 'New Translation' }}</span>
      </div>
      <div v-else class="flex items-center gap-3 font-display">
        <FontAwesomeIcon :icon="faLanguage" />
        <span>Localization</span>
      </div>
    </template>
    <div v-if="store.editingTranslation" :key="`${lastUpdated}-${editingId}`" class="flex flex-col gap-4">
      <AppLanguageSelector v-if="!store.editingTranslation.id" v-model="store.editingTranslation.locale"
        label="Language" allows-empty class="w-full" :disabled-locales="usedLocales"
        :key="store.editingTranslation?.id" />
      <!-- Create AI translation button -->
      <div class="flex gap-2">
        <div class="relative flex-1">
          <UButton size="md" :disabled="!store.editingTranslation?.locale.id || isActive"
            :label="store.editingTranslation?.id ? 'Rewrite translations with AI' : 'Create AI translation'"
            color="hd-primary" block @click="createAiTranslation">
            <template #leading>
              <FontAwesomeIcon :icon="isActive ? faSpinnerThird : faMagicWandSparkles" :spin="isActive" fixed-width />
            </template>
          </UButton>
          <canvas ref="aiCanvas" class="absolute top-0 left-0 w-full h-full z-10 pointer-events-none" />
        </div>
        <UButton v-if="store.editingTranslation.id" :disabled="isActive" size="md" square
          class="w-9 items-center justify-center" color="gray" label="Delete"
          @click="store.deleteTranslation(store.editingTranslation.id)">
          <FontAwesomeIcon :icon="faTrash" />
        </UButton>
      </div>

      <div class="flex flex-col gap-2">
        <template v-for="translateable in translatables" :key="translateable.key">
          <UFormGroup>
            <template #label>
              <div class="flex items-center gap-1 mb-1">
                <span>{{ translateable.label }}</span>
              </div>
            </template>
            <LazyHdInput auto-refresh :model-value="translateable.value"
              @update:model-value="translateable.update($event)" :fields="store.builderFields"
              :mode="typeof translateable.defaultValue === 'string' ? 'text' : 'json'">
              <template v-if="typeof translateable.defaultValue === 'object'" #full-bubble-menu="{ editor }">
                <BubbleMenu plugin-key="main-menu" :editor="editor" :tippy-options="{ duration: 100 }">
                  <MailEditorMenu :editor="editor" />
                </BubbleMenu>
                <BubbleMenu plugin-key="link-menu" :editor="editor" :tippy-options="{ duration: 100 }" :should-show="({ from, to }) => {
                  return store.editor?.isActive('link') && from === to
                }">
                  <MailMenuLink show :editor="editor" :fields="store.builderFields"
                    :item="{ icon: faLink, isActive: editor?.isActive('link') }" />
                </BubbleMenu>
              </template>
            </LazyHdInput>
            <template
              v-if="translateable.defaultValue && (typeof translateable.defaultValue === 'string' ? translateable.defaultValue.trim().length > 0 : translateable.defaultValue?.content?.[0]?.content?.length > 0)"
              #description>
              <div class="border-l-2 border-gray-200 pl-2 mb-2 mt-1">
                <LazyHdInput disabled :model-value="translateable.defaultValue" :fields="store.builderFields"
                  variant="none" :padded="false"
                  :mode="typeof translateable.defaultValue === 'string' ? 'text' : 'json'" />
              </div>
            </template>
          </UFormGroup>
        </template>
      </div>
    </div>
    <div v-else class="flex flex-wrap gap-4">
      <AppLanguageSelector v-model="store.locale" label="Default language" allows-empty class="w-full"
        :disabled-locales="usedLocales" />


      <UFormGroup label="Auto Translate" class="w-full">
        <MailBlockSettingBaseOptions size="md" v-model="store.autoTranslate" default="auto" :options="[
          { label: 'Enabled', value: true },
          { label: 'Disabled', value: false },
        ]" />
      </UFormGroup>

      <div class="flex flex-wrap gap-1 w-full">
        <h4 class="text-sm font-medium text-gray-700">Translations</h4>
        <UButton v-for="translation in store.translations" :key="translation.locale.id" :label="translation.locale.name"
          block size="md" color="white" class="justify-start" @click="edit(translation)" />
        <UButton block size="md" color="white" class="justify-start" @click="create">
          <FontAwesomeIcon :icon="faPlus" />
          <span>New Translation</span>
        </UButton>
      </div>
    </div>
    <template v-if="store.editingTranslation" #footer>
      <div class="grid grid-cols-2 gap-2">
        <UButton :label="store.editingTranslation.id ? 'Update' : 'Create'" color="primary" block
          @click="store.saveTranslation"
          :disabled="store.savingTranslation || !store.editingTranslation?.locale.id || isActive" />
        <UButton label="Cancel" color="gray" block @click="cancel" :disabled="isActive" />
      </div>
    </template>
  </AutomationSideCard>
</template>
